import { axiosInterceptor, apiCall } from 'services'

// This service previously built an ABSOLUTE base URL from a NODE_ENV ternary,
// so every production build sent its chat requests to
// https://app.mydealteams.com/api/chats/... regardless of where the app was
// actually running. On any other origin the browser refused them:
//
//   Access to XMLHttpRequest at 'https://app.mydealteams.com/api/chats/...'
//   from origin 'http://localhost:5000' blocked by CORS policy
//
// The API is same-origin, so the shared relative base ('/api') is correct here
// exactly as it is in every other service. Note the paths below already start
// with '/api', so the base stays empty to avoid doubling the prefix.
const api = axiosInterceptor('')

interface AddChatEventProps {
	projectId?: string
	/** Namespaced room key, e.g. `portfolio:<id>`. See backend/utilities/chatRooms.js. */
	roomId?: string
	eventId?: string
	receiverIds: string[]
	message: string
	attachments?: any[]
	/** Idempotency key. Reusing it on retry returns the original message. */
	clientId?: string
}

const chatService = () => {
	const addChatEvent = async ({
		projectId,
		roomId,
		eventId,
		receiverIds,
		message,
		attachments,
		clientId,
	}: AddChatEventProps) => {
		const chatData = {
			projectid: projectId,
			roomId,
			eventId,
			receiverIds: receiverIds.length > 0 ? receiverIds.map(id => ({ receiverId: id })) : undefined,
			message,
			attachments,
			clientId,
		}

		return await apiCall(api.post('/api/chats', chatData))
	}

	/**
	 * Returns `{ header, messages, hasMore, readState }` for a thread.
	 *
	 * `messages` is one page, oldest-first, and includes the header document as
	 * the thread's first message only when `hasMore` is false — the header
	 * carries the text of the message that opened the conversation, so it
	 * belongs at the start of history and nowhere else.
	 *
	 * `before` walks back through history for "load earlier".
	 */
	const getChatsByEventId = async (eventId: string, opts: { before?: string } = {}) => {
		const query = opts.before ? `?before=${encodeURIComponent(opts.before)}` : ''
		return await apiCall(api.get(`/api/chats/event/${eventId}${query}`))
	}

	/** Edits the text of your own most recent message in a thread. */
	const editMessage = async (messageId: string, message: string) =>
		await apiCall(api.patch(`/api/chats/messages/${messageId}`, { message }))

	/**
	 * Sets your reaction on a message. The same emoji again clears it, a
	 * different one replaces it, and an empty string clears it.
	 */
	const reactToMessage = async (messageId: string, emoji: string) =>
		await apiCall(api.put(`/api/chats/messages/${messageId}/reactions`, { emoji }))

	const markThreadRead = async (eventId: string, lastReadMessageId?: string) =>
		await apiCall(api.post(`/api/chats/threads/${eventId}/read`, { lastReadMessageId }))

	const markThreadDelivered = async (eventId: string, lastDeliveredMessageId?: string) =>
		await apiCall(
			api.post(`/api/chats/threads/${eventId}/delivered`, { lastDeliveredMessageId }),
		)

	const getChatsByRoomId = async projectId => await apiCall(api.get(`/api/chats/room/${projectId}`))

	const getChatsByPortfolioId = async portfolioId =>
		await apiCall(api.get(`/api/chats/portfolio/${portfolioId}`))

	// `getChatHeadersByUserId` was removed with its route: it took the user id
	// from the URL, so it served any user's inbox. Headers come from the
	// `chatHeaders` Redux slice, which uses the token-derived /api/chats/headers.

	return {
		addChatEvent,
		getChatsByEventId,
		getChatsByRoomId,
		getChatsByPortfolioId,
		editMessage,
		reactToMessage,
		markThreadRead,
		markThreadDelivered,
	}
}

export default chatService
